import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { ASSETS } from "../data";

const links = [
  { label: "The Pack", href: "#pack" },
  { label: "Protocol", href: "#protocol" },
  { label: "Compare", href: "#compare" },
  { label: "1-on-1", href: "#one-on-one" },
  { label: "FAQ", href: "#faq" },
];

export const Navbar = ({ onBuy }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const target = document.querySelector(href);
    if (!target) return;
    if (window.lenis) window.lenis.scrollTo(target, { offset: -80, duration: 1.2 });
    else target.scrollIntoView({ behavior: "smooth" });
  };

  const toTop = (e) => {
    e.preventDefault();
    if (window.lenis) window.lenis.scrollTo(0, { duration: 1.2 });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      style={{ top: "var(--banner-h, 0px)" }}
      className={`fixed left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-2xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo-link" onClick={toTop} className="flex items-center gap-3 group">
          <img src={ASSETS.logo} alt="Ghost The Grid" className="h-10 w-10 object-cover rounded-sm" />
          <span className="font-display font-extrabold tracking-tight text-lg leading-none uppercase">Ghost<span className="text-accent">·</span>The<span className="text-accent">·</span>Grid</span>
        </a>

        <div className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => go(l.href)}
              className="font-mono text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-buy-button"
            onClick={onBuy}
            className="font-mono text-xs uppercase tracking-[0.2em] bg-accent text-white px-6 py-3 hover:bg-white hover:text-black transition-colors duration-300"
          >
            Get The Pack
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Open menu"
          aria-expanded={open}
          className="md:hidden flex items-center gap-2 border border-accent/70 bg-accent/10 px-3.5 py-2 text-white hover:bg-accent hover:text-white transition-colors duration-300"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={18} /> : <Menu size={18} />}
          <span className="font-mono text-[11px] uppercase tracking-[0.25em]">{open ? "Close" : "Menu"}</span>
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 backdrop-blur-2xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button key={l.href} onClick={() => go(l.href)} className="font-mono text-sm uppercase tracking-[0.2em] text-white/70 text-left">
                  {l.label}
                </button>
              ))}
              <button data-testid="mobile-buy-button" onClick={() => { setOpen(false); onBuy(); }} className="font-mono text-sm uppercase tracking-[0.2em] bg-accent text-white px-6 py-3 mt-2">
                Get The Pack
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
